import "bootstrap/dist/css/bootstrap.min.css";
import React, { useEffect, useState } from "react";
import { FiLock, FiMail, FiUser, FiLoader, FiAlertCircle, FiCheckCircle, FiScissors } from "react-icons/fi";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useAuthActions } from "../../hooks/useAuthActions";
import { useNotification } from "../../context/NotificationContext";

export default function Signup() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { signupUser } = useAuthActions();
  const { showNotification } = useNotification();
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/uploader");
    }
  }, [isAuthenticated, navigate]);

  const handleSignup = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      await signupUser({ userName: name, email, password });
      showNotification("Account created successfully! Redirecting to login...", "success");
      setName("");
      setEmail("");
      setPassword("");
      setTimeout(() => navigate("/signin"), 1500);
    } catch (err: any) {
      showNotification(err.response?.data?.message || err.message || "Signup failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex flex-column hero-bg pt-5" style={{ fontFamily: "var(--font-family)" }}>
        <div className="blob animate-morph bg-primary" style={{ top: '-10%', right: '-10%', width: '600px', height: '600px', opacity: 0.1 }}></div>
        <div className="blob animate-morph bg-secondary" style={{ bottom: '-10%', left: '-10%', width: '500px', height: '500px', opacity: 0.1, animationDelay: '-2s' }}></div>
        
        <div className="row g-0 w-100 flex-grow-1 position-relative z-1 mt-4 flex-lg-row-reverse">
            {/* Right Side - Branding */}
            <div className="col-lg-6 d-none d-lg-flex flex-column justify-content-between p-5" 
                 style={{ 
                     background: "var(--primary-gradient)",
                     borderRadius: "40px 0 0 40px"
                 }}>
                <div className="position-relative z-1 d-flex justify-content-end">
                    <Link to="/" className="d-flex align-items-center gap-3 text-white text-decoration-none mb-5">
                        <div className="bg-white text-primary p-2 rounded-3 shadow-lg">
                            <FiScissors size={28} />
                        </div>
                        <span className="fs-3 fw-900 tracking-tight">SmartShrink</span>
                    </Link>
                </div>

                <div className="position-relative z-1 mb-5">
                    <h1 className="display-4 fw-900 text-white mb-4" style={{ letterSpacing: "-1px", lineHeight: 1.1 }}>
                        Start your journey.<br/>
                        <span style={{ color: "var(--accent)" }}>Join us today.</span>
                    </h1>
                    <p className="fs-6 text-white opacity-90 max-w-md mb-4 lh-lg">
                        SmartShrink is a unified AI intelligence platform designed to accelerate your career and projects:
                    </p>
                    <ul className="list-unstyled text-white opacity-90 d-flex flex-column gap-3 mb-0">
                        <li className="d-flex align-items-center gap-2"><FiCheckCircle className="text-accent" /> Neural Document Analysis</li>
                        <li className="d-flex align-items-center gap-2"><FiCheckCircle className="text-accent" /> Architect Blueprint Studio</li>
                        <li className="d-flex align-items-center gap-2"><FiCheckCircle className="text-accent" /> Interactive Career Sync</li>
                        <li className="d-flex align-items-center gap-2"><FiCheckCircle className="text-accent" /> Edu-Pulse Knowledge Kits</li>
                    </ul>
                </div>

                <div className="position-relative z-1">
                    <div className="glass p-4 rounded-5 border-0 shadow-xl">
                        <div className="d-flex gap-1 text-warning mb-3 fs-5">
                            {"★★★★★".split("").map((star, i) => <span key={i}>{star}</span>)}
                        </div>
                        <p className="text-dark fw-bold mb-4 fs-5 fst-italic" style={{ lineHeight: 1.4 }}>
                          "SmartShrink's AI analyzer completely transformed how I apply for jobs. The compression tools are just the cherry on top!"
                        </p>
                        <div className="d-flex align-items-center gap-3">
                            <img src="https://i.pravatar.cc/100?img=47" alt="User" className="rounded-circle shadow-sm" style={{ width: "48px", height: "48px" }} />
                            <div>
                                <p className="text-dark fw-900 mb-0">Sarah Jenkins</p>
                                <p className="text-muted small fw-bold mb-0">Product Designer</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Left Side - Signup Form */}
            <div className="col-lg-6 d-flex align-items-center justify-content-center p-4 p-md-5 position-relative">
                {/* Floating Decorative Elements */}
                <div className="position-absolute top-0 start-0 p-5 mt-5 d-none d-lg-block animate-float">
                    <div className="bg-white p-3 rounded-circle shadow-lg text-primary">
                        <FiUser size={24} />
                    </div>
                </div>
                <div className="position-absolute bottom-0 end-0 p-5 mb-5 d-none d-lg-block animate-float-delayed">
                    <div className="bg-white p-3 rounded-circle shadow-lg text-secondary">
                        <FiCheckCircle size={24} />
                    </div>
                </div>

                <div className="w-100 animate-fade-in-up card-premium glass p-4 p-sm-5 shadow-2xl position-relative z-10" style={{ maxWidth: "480px", borderRadius: "2rem" }}>
                    <div className="d-lg-none d-flex align-items-center gap-3 mb-5 justify-content-center">
                        <div className="bg-primary text-white rounded-3 p-2 d-flex align-items-center justify-content-center shadow-md">
                            <FiScissors size={24} />
                        </div>
                        <span className="fs-3 fw-900 text-dark tracking-tight">SmartShrink</span>
                    </div>

                    <div className="mb-5 text-center">
                        <div className="badge bg-primary bg-opacity-10 text-primary rounded-pill px-3 py-1 fw-900 mb-3 tracking-widest">JOIN THE COMMUNITY</div>
                        <h2 className="fw-900 text-dark mb-2" style={{ letterSpacing: "-1px" }}>Create account</h2>
                        <p className="text-muted fw-medium">Join our professional community today.</p>
                    </div>


                    <form onSubmit={handleSignup}>
                        <div className="mb-4">
                            <label htmlFor="name" className="form-label small fw-900 text-dark mb-2 ms-1">Full Name</label>
                            <div className="input-group input-group-lg rounded-4 overflow-hidden bg-white shadow-sm border focus-ring-container">
                                <span className="input-group-text bg-transparent border-0 text-muted px-3">
                                    <FiUser size={18} />
                                </span>
                                <input
                                    type="text"
                                    id="name"
                                    className="form-control bg-transparent border-0 px-2 py-3 fs-6"
                                    placeholder="John Doe"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <label htmlFor="email" className="form-label small fw-900 text-dark mb-2 ms-1">Email Address</label>
                            <div className="input-group input-group-lg rounded-4 overflow-hidden bg-white shadow-sm border focus-ring-container">
                                <span className="input-group-text bg-transparent border-0 text-muted px-3">
                                    <FiMail size={18} />
                                </span>
                                <input
                                    type="email"
                                    id="email"
                                    className="form-control bg-transparent border-0 px-2 py-3 fs-6"
                                    placeholder="name@company.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="mb-4">
                            <label htmlFor="password" className="form-label small fw-900 text-dark mb-2 ms-1">Password</label>
                            <div className="input-group input-group-lg rounded-4 overflow-hidden bg-white shadow-sm border focus-ring-container">
                                <span className="input-group-text bg-transparent border-0 text-muted px-3">
                                    <FiLock size={18} />
                                </span>
                                <input
                                    type="password"
                                    id="password"
                                    className="form-control bg-transparent border-0 px-2 py-3 fs-6"
                                    placeholder="Create a strong password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="btn-premium w-100 py-3 fs-5 d-flex align-items-center justify-content-center gap-2 mt-4 shadow-lg"
                            disabled={loading}
                        >
                            {loading ? <FiLoader className="animate-spin" /> : null}
                            {loading ? "Creating Account..." : "Sign Up"}
                        </button>
                        
                        <div className="mt-5 text-center">
                            <p className="text-muted mb-0 fw-medium">
                                Already have an account?{" "}
                                <Link to="/signin" className="text-primary fw-900 text-decoration-none ms-1 border-bottom border-primary border-2 pb-1 hover-opacity-75 transition-all">
                                    Log in here
                                </Link>
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <style>{`
            .animate-spin { animation: spin 1s linear infinite; }
            @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            .focus-ring-container { transition: all 0.3s ease; }
            .focus-ring-container:focus-within {
                border-color: var(--primary) !important;
                box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1) !important;
                background: white !important;
            }
            .form-control:focus { box-shadow: none; }
        `}</style>
    </div>
  );
}